import { debounce } from '@fe/core';
import { AttributionControl } from 'maplibre-gl';
import React, { CSSProperties, useEffect, useLayoutEffect } from 'react';
import { OnMapControlsDisplayEnum } from '../../common/enums';
import { UDViewMapModel } from '../../udView/udview';
import { useHoverVisible } from '../features/home/hooks/use-hover-visible';
import { useAppSelector } from '../hooks/store-hooks';
import { FeMap } from './map/fe-map';

export const MIN_ZOOM = 0;
export const MAX_ZOOM = 22;

export interface MaplibreMapProps {
    initialState?: UDViewMapModel;
    className?: string;
    style?: CSSProperties;
    children?: React.ReactNode;
    onCreate?: (map: FeMap) => void;
    styleLoaded?: (map: FeMap) => void;
}

export interface MaplibreMapRef {
    map: FeMap | null;
}

export default function MaplibreMap(props: MaplibreMapProps) {
    const mapRef = React.useRef<FeMap | null>(null);
    const mapContainerRef = React.useRef<HTMLDivElement | null>(null);
    const attributionControlRef = React.useRef<AttributionControl | null>(null);
    const id = React.useId();
    const contributionHover = useHoverVisible();
    const onMapControlsDisplaySettings = useAppSelector((x) => x.settings.OnMapControlsDisplay);

    const makeControlStyle = (visible: boolean): CSSProperties => ({
        opacity:
            onMapControlsDisplaySettings === OnMapControlsDisplayEnum.Hide
                ? 0
                : onMapControlsDisplaySettings === OnMapControlsDisplayEnum.OnlyWhenActive
                  ? visible
                      ? 1
                      : 0
                  : 1,
        transition: 'opacity 0.3s ease'
    });

    useLayoutEffect(() => {
        let mounted = true;
        Promise.resolve().then(() => {
            if (!mounted) {
                return;
            }
            const map = new FeMap({
                constraintToSingleWorld: true,
                container: id,
                boxZoom: true,
                attributionControl: false,
                maxPitch: 85,
                renderWorldCopies: true
            });
            mapContainerRef.current = document.getElementById(id) as HTMLDivElement;

            const attributionControl = new AttributionControl({ compact: true });
            map.addControl(attributionControl, 'bottom-left');
            attributionControlRef.current = attributionControl;

            map.once('idle', () => {
                attributionControl._updateCompactMinimize?.();
            });

            props.onCreate?.(map);
            mapRef.current = map;

            // No need to unsubscribe, map.remove remove all subscriptions.
            map.on('style.load', () => {
                props.styleLoaded?.(map);
            });
        });

        return () => {
            mounted = false;
            if (mapRef.current) {
                mapRef.current?.remove();
                mapRef.current._listeners = {};
                mapRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const debouncedResize = debounce(() => {
            mapRef.current?.resize();
        }, 0);

        const handleResize = () => {
            debouncedResize();
        };

        const resizer = new ResizeObserver(handleResize);

        if (mapContainerRef.current) {
            resizer.observe(mapContainerRef.current);
        }

        return () => {
            resizer.disconnect();
        };
    }, [mapRef.current, mapContainerRef.current]);

    useEffect(() => {
        const container = attributionControlRef.current?._container;
        if (!container) {
            return;
        }
        container.addEventListener('mouseenter', contributionHover.onMouseEnter);
        container.addEventListener('mouseleave', contributionHover.onMouseLeave);
        return () => {
            container.removeEventListener('mouseenter', contributionHover.onMouseEnter);
            container.removeEventListener('mouseleave', contributionHover.onMouseLeave);
        };
    }, [attributionControlRef.current, contributionHover.onMouseEnter, contributionHover.onMouseLeave]);

    useEffect(() => {
        const container = attributionControlRef.current?._container;
        if (!container) {
            return;
        }
        const style = makeControlStyle(contributionHover.visible);
        Object.assign(container.style, style);
    }, [contributionHover.visible, onMapControlsDisplaySettings]);

    return (
        <div className={props.className} id={id} style={props.style}>
            {props.children}
        </div>
    );
}
